import { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getHttp, postHttp } from 'src/api'
import { PATH_API, PATH_URL } from 'src/helpers'
import { SESSION_NAME_LOCAL } from 'src/helpers/app'
import { BoardItem, PinItem, QuizType, SectionItem } from 'src/pages/shared'
import { QuizQuestionContext, SCREEN_QUIZ_QUESTION } from '../../contextAPI'

export interface BoardDetail {
  board?: BoardItem
  pins: PinItem[]
  sections: SectionItem[]
}

export const useImportPins = (boardId: string) => {
  const { setQuizScreen } = useContext(QuizQuestionContext)
  const history = useHistory()
  const [boardDetail, setBoardDetail] = useState<BoardDetail>({
    pins: [],
    sections: [],
  })
  const [loading, setLoading] = useState(false)
  const [selectedSection, setSelectedSection] = useState<SectionItem>()
  const [filteredPins, setFilteredPins] = useState<PinItem[]>([])

  const canImport = useMemo(() => {
    return boardDetail?.board && filteredPins.some((pin) => pin.isSelected)
  }, [boardDetail?.board, filteredPins])

  const isSelectedAll = useMemo(() => {
    return filteredPins.every((pin) => pin.isSelected)
  }, [filteredPins])

  useEffect(() => {
    if (loading || !boardId) return
    handleGetBoardDetail(boardId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId])

  useEffect(() => {
    if (!selectedSection) {
      setFilteredPins((pre) =>
        boardDetail.pins.map((pin) => ({
          ...pin,
          isSelected: pre.some((p) => p.id === pin.id && p.isSelected),
        }))
      )
    } else {
      setFilteredPins((pre) =>
        boardDetail.pins
          .filter((pin) => pin.boardSectionId === selectedSection.id)
          .map((pin) => ({
            ...pin,
            isSelected: pre.some((p) => p.id === pin.id && p.isSelected),
          }))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, boardDetail.pins])

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleNext = () => {}
  const handleBack = () => {
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push({
        pathname: PATH_URL.PINTEREST_LINK_YOUR_BOARD,
        search: new URLSearchParams({ quizType: QuizType.PINTEREST }).toString(),
      })
      setQuizScreen(SCREEN_QUIZ_QUESTION.PINTEREST_LINK_YOUR_BOARD)
    }
  }

  const toggleSelectSection = (section: SectionItem) => {
    if (selectedSection?.id === section.id) {
      setSelectedSection(undefined)
    } else {
      setSelectedSection(section)
    }
  }

  const handleSelectPin = (pin: PinItem) => {
    setFilteredPins((pre) => pre.map((p) => ({ ...p, isSelected: p.id === pin.id ? !p.isSelected : p.isSelected })))
  }

  const handleSelectAllPins = () => {
    setFilteredPins((pre) => pre.map((p) => ({ ...p, isSelected: !isSelectedAll })))
  }

  const handleGetBoardDetail = async (boardId: string) => {
    const sessionId = localStorage.getItem(SESSION_NAME_LOCAL)
    if (!sessionId || !boardId) return

    setLoading(true)
    try {
      const result = await getHttp(PATH_API.getBoardDetail(sessionId, boardId))
      setBoardDetail(result.data)
    } catch (error) {
      console.log('[handleGetBoardDetail] error: ', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImportPinsFromBoard = async () => {
    if (!canImport || !boardDetail?.board) return

    setLoading(true)
    try {
      const sessionId = localStorage.getItem(SESSION_NAME_LOCAL)
      const currentTempData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
      const data = {
        sessionId,
        boardId: boardDetail.board.id,
        boardName: boardDetail.board.name,
        firstName: currentTempData?.customerInfo?.firstName,
        lastName: currentTempData?.customerInfo?.lastName,
        partnerName: currentTempData?.customerInfo?.partnerName,
        pins: filteredPins
          .filter((pin) => pin.isSelected)
          .map((pin) => ({
            id: pin.id,
            name: pin.title || pin.description,
          })),
      }
      await postHttp(PATH_API.importPinsFromBoard(boardDetail.board.id), data)

      // Skip color palette page and go directly to guest count when in Pinterest flow
      console.log('[Pinterest Flow] Skipping color palette page, going directly to guest count')
      history.push({
        pathname: PATH_URL.GUEST_COUNT,
        search: new URLSearchParams({ quizType: QuizType.PINTEREST }).toString(),
      })
      setQuizScreen(SCREEN_QUIZ_QUESTION.GUEST_COUNT)
    } catch (error) {
      console.error('handleImportPinsFromBoard', error)
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    canImport,
    isSelectedAll,
    boardDetail,
    selectedSection,
    filteredPins,
    handleNext,
    handleBack,
    handleImportPinsFromBoard,
    handleSelectPin,
    handleSelectAllPins,
    toggleSelectSection,
  }
}
